/**
 * Créditos de IA — balance de la empresa + cupo gratis diario + compra por tarjeta.
 *
 * Modelo: cada usuario tiene N consultas gratis/día; agotadas, el bot consume
 * créditos de la empresa (bolsa compartida). Los créditos se compran por tarjeta
 * (mismo hub que la suscripción). Ver AiCreditController + AssistantController.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from './api';

export interface CreditPack {
  id: string;
  credits: number;
  price: number;
  currency: string;
}

export type CreditScope = 'personal' | 'company';

export interface AiCreditsState {
  /** Créditos personales del usuario (sólo los usa él). */
  balance_personal: number;
  /** Créditos de la empresa (bolsa compartida). */
  balance_company: number;
  /** Suma de ambos. */
  balance_total: number;
  free_daily: number;
  used_today: number;
  free_remaining: number;
  currency: string;
  packs: CreditPack[];
  /** true si el usuario puede comprar para toda la empresa (COMPANY_ADMIN). */
  is_admin: boolean;
}

/** Meta de consumo que devuelve cada turno del chat (para actualizar el contador). */
export interface UsageMeta {
  mode: 'free' | 'credit';
  free_daily: number;
  free_remaining: number;
  balance: number;
}

export const aiCreditsKey = ['billing', 'ai-credits'] as const;

export function useAiCredits(enabled = true) {
  return useQuery({
    queryKey: aiCreditsKey,
    queryFn: () => api.get<AiCreditsState>('/billing/ai-credits'),
    staleTime: 30_000,
    enabled,
    retry: false,
  });
}

export interface BuyResponse {
  payment_id: string;
  checkout_url: string;
  amount: number;
  currency: string;
  credits: number;
}

/**
 * Inicia la compra de un paquete → devuelve la URL de checkout del hub.
 * scope 'company' (bolsa compartida) requiere ser admin; 'personal' cualquiera.
 */
export function buyCreditPack(packId: string, scope: CreditScope): Promise<BuyResponse> {
  return api.post<BuyResponse>('/billing/ai-credits/buy', { pack_id: packId, scope });
}
